import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowUpRight, CalendarDays, Clock, Sparkles } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppFloat from '../components/WhatsAppFloat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    upsertMeta('description', 'Guias práticos de SEO, sites e marketing digital para empresas de Belo Horizonte que querem atrair clientes melhores.');
    upsertCanonical(`${window.location.origin}/blog`);
    document.title = 'Blog de SEO e Marketing Digital em Belo Horizonte | Thesis';
  }, []);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/posts`)
      .then((res) => { if (active) setPosts(res.data || []); })
      .catch(() => { if (active) setPosts([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  return (
    <main className="relative min-h-screen bg-[#0A0B0E] text-[#F5F1E8] overflow-hidden">
      <Header />
      <section className="relative min-h-[62vh] flex flex-col justify-end pt-32 pb-14 md:pb-20 grain">
        <div className="absolute inset-0 opacity-60">
          <img
            src="https://images.unsplash.com/photo-1749544812189-193ccae5e2f4?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
            alt="Luz dourada abstrata em fundo escuro"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E] via-[#0A0B0E]/78 to-[#0A0B0E]/20" />
        </div>
        <div className="thesis-container relative z-10" data-testid="blog-list-container">
          <div className="max-w-[980px] reveal is-visible">
            <span data-testid="golden-pill-tag" className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-1 text-[11px] font-mono-label text-[#D4AF37]">
              <Sparkles size={12} /> GUIAS SEO · BH
            </span>
            <h1 data-testid="blog-list-title" className="mt-6 font-editorial text-[56px] sm:text-[86px] lg:text-[118px] leading-[0.86] tracking-normal text-[#F5F5F5]">
              Inteligência editorial para marcas que querem ser encontradas.
            </h1>
            <p className="mt-7 max-w-2xl text-[16px] md:text-[19px] leading-relaxed text-[#A3A3A3]">
              Análises práticas sobre sites, marketing e SEO local em Belo Horizonte — escritas para quem precisa transformar presença digital em clientes.
            </p>
          </div>
        </div>
      </section>

      <section className="thesis-container py-14 md:py-24">
        {!loading && posts.length > 0 && (
          <div className="mb-8 md:mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <span className="font-mono-label text-[#D4AF37]">Biblioteca Thesis</span>
              <h2 className="mt-3 font-editorial text-[38px] md:text-[58px] leading-none text-[#F5F5F5]">Últimos guias publicados</h2>
            </div>
            <p className="max-w-md text-[14px] leading-relaxed text-[#A3A3A3]">
              Conteúdo direto para decisões melhores: site, conversão, SEO local e presença digital em BH.
            </p>
          </div>
        )}

        {loading && (
          <div data-testid="blog-loading-state" className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => <div key={i} className="h-[420px] glass rounded-[8px] animate-pulse" />)}
          </div>
        )}

        {!loading && posts.length === 0 && (
          <div data-testid="blog-empty-state" className="border border-white/10 rounded-[8px] py-20 px-6 text-center bg-white/[0.02]">
            <p className="font-editorial text-[42px] md:text-[64px] leading-none text-[#F5F5F5]">O primeiro artigo está sendo preparado.</p>
            <p className="mt-4 text-[#A3A3A3]">Estamos preparando uma biblioteca objetiva para negócios que querem crescer em Belo Horizonte.</p>
          </div>
        )}

        {!loading && posts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8">
            {posts.map((post, index) => <ArticleCard key={post.id} post={post} featured={index === 0} />)}
          </div>
        )}
      </section>

      <Footer />
      <WhatsAppFloat />
    </main>
  );
}

function upsertMeta(name, content) {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function upsertCanonical(href) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

function ArticleCard({ post, featured }) {
  return (
    <article
      data-testid={`article-card-${post.slug}`}
      className={`group relative overflow-hidden rounded-[8px] border border-white/10 bg-[#111111] transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1 hover:border-white/25 hover:shadow-2xl hover:shadow-black/50 ${featured ? 'md:col-span-2' : ''}`}
    >
      <a href={`/blog/${post.slug}`} data-testid={`article-card-link-${post.slug}`} className="block cursor-hover">
        <div className={`relative overflow-hidden ${featured ? 'aspect-[16/8]' : 'aspect-[16/10]'}`}>
          <img src={post.cover_image} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E] via-[#0A0B0E]/45 to-transparent" />
          <div className="absolute left-5 top-5 flex flex-wrap gap-2">
            {(post.seo_metadata?.keywords || []).slice(0, 2).map((tag) => (
              <span key={tag} data-testid={`golden-pill-tag-${post.slug}-${tag}`} className="rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-1 text-[10px] font-mono-label text-[#D4AF37]">{tag}</span>
            ))}
          </div>
        </div>
        <div className="p-6 md:p-7">
          <div className="flex flex-wrap items-center gap-4 text-[11px] font-mono-label text-[#737373]">
            <span data-testid={`article-date-${post.slug}`} className="inline-flex items-center gap-2"><CalendarDays size={13} /> {new Date(post.created_at).toLocaleDateString('pt-BR')}</span>
            <span data-testid={`article-read-time-${post.slug}`} className="inline-flex items-center gap-2"><Clock size={13} /> {post.read_time_minutes} min</span>
          </div>
          <h2 data-testid={`article-title-${post.slug}`} className="mt-5 font-editorial text-[36px] md:text-[46px] leading-[0.94] tracking-normal text-[#F5F5F5]">
            {post.title}
          </h2>
          <p data-testid={`article-excerpt-${post.slug}`} className="mt-5 text-[15px] leading-relaxed text-[#A3A3A3]">{post.excerpt}</p>
          <span data-testid={`blog-read-link-${post.slug}`} className="mt-7 inline-flex items-center gap-2 text-[13px] font-semibold text-[#D4AF37]">
            Ler artigo <ArrowUpRight size={15} />
          </span>
        </div>
      </a>
    </article>
  );
}